import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, ChevronDown, ChevronRight, Network, LayoutGrid, Crown, Briefcase, UserCog, ShieldCheck, ClipboardList, UserRound } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['bg-primary', 'bg-info', 'bg-success', 'bg-warning', 'bg-error', 'bg-cyan-500'];

const GROUP_META = {
  'C-Level':     { Icon: Crown,         className: 'from-warning to-error',   accent: 'text-warning',  bg: 'bg-warning/5 border-warning/30' },
  'Directors':   { Icon: Briefcase,     className: 'from-primary to-info',    accent: 'text-primary',  bg: 'bg-primary/5 border-primary/30' },
  'Managers':    { Icon: UserCog,       className: 'from-info to-success',    accent: 'text-info',     bg: 'bg-info/5 border-info/30' },
  'Supervisors': { Icon: ShieldCheck,   className: 'from-success to-success', accent: 'text-success',  bg: 'bg-success/5 border-success/30' },
  'Officers':    { Icon: ClipboardList, className: 'from-muted to-muted',     accent: 'text-foreground', bg: 'bg-muted/30 border-border' },
  'Staff':       { Icon: UserRound,     className: 'from-muted to-muted',     accent: 'text-muted-foreground', bg: 'bg-muted/20 border-border' },
};

function PersonCard({ person, accent }) {
  const initials = (person.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div
      className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:shadow-md hover:-translate-y-0.5 transition-all cursor-default"
      title={`${person.name}\n${person.title || ''}\n${person.department || ''}`}
      data-testid={`org-person-${person.id}`}
    >
      <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${COLORS[Math.abs(initials.charCodeAt(0)) % COLORS.length]} text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate">{person.name}</p>
        <p className={`text-[10px] truncate ${accent}`}>{person.title || 'No title'}</p>
      </div>
      {person.department && (
        <Badge variant="outline" className="text-[9px] hidden md:inline-flex whitespace-nowrap">{person.department}</Badge>
      )}
    </div>
  );
}

function NodeItem({ node, depth, allNodes }) {
  const [open, setOpen] = useState(depth < 2);
  const kids = allNodes.filter(n => n.parent === node.id);
  const initials = (node.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div>
      <div
        className={`flex items-center gap-3 p-3 rounded-xl border hover:shadow-md transition-all cursor-pointer ${depth === 0 ? 'border-primary/30 bg-primary/5' : 'border-border'}`}
        onClick={() => kids.length > 0 && setOpen(!open)}
        data-testid={`org-node-${node.id}`}
      >
        {kids.length > 0 && <span className="text-muted-foreground">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>}
        <div className={`w-9 h-9 rounded-full ${COLORS[depth % COLORS.length]} flex items-center justify-center text-white text-xs font-bold shadow-sm`}>{initials}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{node.name}</p>
          <p className="text-[10px] text-muted-foreground truncate">{node.title || 'No title'}</p>
        </div>
        {node.position_group && <Badge variant="outline" className="text-[9px] hidden sm:inline-flex">{node.position_group}</Badge>}
        {node.department && <Badge variant="outline" className="text-[9px] hidden md:inline-flex">{node.department}</Badge>}
        {kids.length > 0 && <Badge className="text-[9px] bg-muted text-muted-foreground border-0">{kids.length}</Badge>}
      </div>
      {open && kids.length > 0 && (
        <div className="ml-6 mt-1 pl-4 border-l-2 border-border space-y-1">
          {kids.map(child => <NodeItem key={child.id} node={child} depth={depth + 1} allNodes={allNodes} />)}
        </div>
      )}
    </div>
  );
}

export default function OrgChartPage() {
  const [flatNodes, setFlatNodes] = useState([]);
  const [roots, setRoots] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [mode, setMode] = useState('position'); // 'position' | 'hierarchy'

  useEffect(() => {
    api.get('/organization/chart').then(res => {
      const tree = res.data?.tree || [];
      setTotal(res.data?.total || 0);
      setGroups(res.data?.groups || []);
      const flat = [];
      const flatten = (nodes, parentId) => {
        for (const n of nodes) {
          flat.push({ id: n.id, name: n.name, title: n.title, department: n.department, position_group: n.position_group, parent: parentId });
          if (n.children) flatten(n.children, n.id);
        }
      };
      flatten(tree, null);
      setFlatNodes(flat);
      setRoots(flat.filter(n => n.parent === null));
    }).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="org-chart-page">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-primary to-info flex items-center justify-center shadow-lg shadow-primary/20">
            <Users size={22} className="text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight">Organization Chart</h1>
            <p className="text-sm text-muted-foreground">{total} employees · grouped by position level</p>
          </div>
          <div className="flex gap-1 bg-muted rounded-xl p-1">
            <Button
              size="sm"
              variant={mode === 'position' ? 'default' : 'ghost'}
              className="rounded-lg text-xs h-7"
              onClick={() => setMode('position')}
              data-testid="org-mode-position"
            >
              <LayoutGrid size={12} className="mr-1" />By Position
            </Button>
            <Button
              size="sm"
              variant={mode === 'hierarchy' ? 'default' : 'ghost'}
              className="rounded-lg text-xs h-7"
              onClick={() => setMode('hierarchy')}
              data-testid="org-mode-hierarchy"
            >
              <Network size={12} className="mr-1" />Hierarchy
            </Button>
          </div>
        </div>

        {loading ? (
          <Card><CardContent className="pt-6"><div className="text-center py-12 text-muted-foreground">Loading...</div></CardContent></Card>
        ) : total === 0 ? (
          <Card><CardContent className="pt-6">
            <div className="text-center py-12">
              <Users size={32} className="mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No organization structure yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Add employees with job titles to build the hierarchy.</p>
            </div>
          </CardContent></Card>
        ) : mode === 'position' ? (
          /* ─── By-position "swim-lane" view — Directors on top, Staff at bottom ─── */
          <div className="space-y-3" data-testid="org-lanes">
            {groups.map((g) => {
              const meta = GROUP_META[g.label] || GROUP_META['Staff'];
              const Icon = meta.Icon;
              return (
                <Card key={g.label} className={`border-2 ${meta.bg}`} data-testid={`org-lane-${g.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.className} flex items-center justify-center shadow`}>
                        <Icon size={15} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className={`text-xs font-bold uppercase tracking-wider ${meta.accent}`}>{g.label}</p>
                        <p className="text-[10px] text-muted-foreground">{g.members.length} {g.members.length === 1 ? 'person' : 'people'} · level {g.level}</p>
                      </div>
                      <Badge variant="outline" className={`${meta.accent} border-current`}>{g.members.length}</Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5">
                      {g.members.map((m) => <PersonCard key={m.id} person={m} accent={meta.accent} />)}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          /* ─── Reporting hierarchy view ─── */
          <Card><CardContent className="pt-6">
            <div className="space-y-2" data-testid="org-tree">
              {roots.map(node => <NodeItem key={node.id} node={node} depth={0} allNodes={flatNodes} />)}
            </div>
          </CardContent></Card>
        )}
      </div>
    </DashboardLayout>
  );
}
